(() => {
  const roots = document.querySelectorAll('[data-rosso-reserve-root]');
  if (!roots.length) return;

  document.documentElement.classList.add('rosso-reserve-pdp-active');

  // This template hides third-party popups (Klaviyo/LoyaltyLion) via CSS. Those
  // apps lock page scroll with `klaviyo-prevent-body-scrolling` + inline overflow
  // while "open"; because the popup is hidden here the lock would orphan and
  // freeze the page. The popup is unconditionally hidden on this template, so
  // releasing the lock whenever it reappears is always safe.
  const releaseOrphanScrollLock = () => {
    if (document.body.classList.contains('klaviyo-prevent-body-scrolling')) {
      document.body.classList.remove('klaviyo-prevent-body-scrolling');
    }
    [document.body, document.documentElement].forEach((el) => {
      if (el.style.overflow === 'hidden') el.style.overflow = '';
      if (el.style.position === 'fixed') el.style.position = '';
    });
  };
  releaseOrphanScrollLock();
  new MutationObserver(releaseOrphanScrollLock).observe(document.body, {
    attributes: true,
    attributeFilter: ['class', 'style'],
  });

  const cartBase = () => {
    const base = window.theme?.routes?.cart || '/cart';
    return base.endsWith('/') ? base.slice(0, -1) : base;
  };

  const fetchCart = async () => {
    const response = await fetch(`${cartBase()}.js`, {
      credentials: 'same-origin',
      headers: {Accept: 'application/json'},
    });

    if (!response.ok) throw new Error('Unable to refresh cart');
    return response.json();
  };

  const cartSurfaceSelectors = [
    '[data-drawer="drawer-cart"].drawer--visible',
    '[data-product-add-popdown-wrapper].is-visible',
    'cart-drawer[open]',
    '.cart-drawer.is-open',
    '.cart-drawer.active',
    '.drawer-cart.is-open',
    '.drawer-cart.active',
    '.ajax-cart--is-visible',
    '.cart-notification.active',
  ];

  const externalCartSurfaceSelectors = cartSurfaceSelectors.filter(
    (selector) => selector !== '[data-drawer="drawer-cart"].drawer--visible'
  );

  const isVisible = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) !== 0
    );
  };

  const isExternalCartSurface = (element) => {
    if (!isVisible(element)) return false;
    if (element.closest('[data-drawer], #PBarNextFrameWrapper, .theme__header, header')) return false;
    if (element.matches('.drawer__underlay, script, style, link')) return false;

    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    const zIndex = Number(style.zIndex);
    const text = (element.textContent || '').replace(/\s+/g, ' ');
    const label = [element.id, element.className, element.getAttribute('role'), element.getAttribute('aria-label')]
      .join(' ')
      .toLowerCase();
    const isLayer = style.position === 'fixed' || style.position === 'sticky' || zIndex >= 900;
    const isRightPanel = rect.right >= window.innerWidth - 80 && rect.left >= window.innerWidth * 0.35;
    const looksLikeCart =
      /(your cart|checkout|subtotal|saving\s+\d+%|delivery frequency|items? in cart)/i.test(text) ||
      /(cart|drawer|modal|recharge|upcart|rebuy|slide|panel|portal)/i.test(label) ||
      element.tagName === 'IFRAME';

    return isLayer && isRightPanel && rect.width >= 220 && rect.height >= 140 && looksLikeCart;
  };

  const hasExternalCartSurface = () => [...document.body.querySelectorAll('*')].some(isExternalCartSurface);

  const hasVisibleExternalCartSurface = () =>
    externalCartSurfaceSelectors.some((selector) => [...document.querySelectorAll(selector)].some(isVisible)) ||
    hasExternalCartSurface();

  const closeCartDrawer = () => {
    const drawer = document.querySelector('[data-drawer="drawer-cart"].drawer--visible');
    if (!drawer) return;

    drawer.dispatchEvent(
      new CustomEvent('theme:drawer:close', {
        bubbles: false,
      })
    );

    window.setTimeout(() => {
      if (!hasVisibleExternalCartSurface() || !drawer.classList.contains('drawer--visible')) return;
      drawer.querySelector('.drawer__close, [aria-label*="close" i]')?.click();
    }, 80);
  };

  // After our AJAX add, a cart app (Rebuy/UpCart/etc.) may open its own cart
  // surface alongside the theme drawer. Suppress the duplicate within a short,
  // bounded window driven by actual DOM changes — instead of blindly polling on
  // fixed timers for 15s, which wasted work and could also close a drawer the
  // shopper reopened later. Observes app-rendered cart DOM; disconnects itself.
  let dedupeObserver = null;
  const dedupeCartSurfaces = () => {
    if (dedupeObserver) dedupeObserver.disconnect();
    let scheduled = false;
    const settle = () => {
      if (scheduled) return;
      scheduled = true;
      window.setTimeout(() => {
        scheduled = false;
        if (hasVisibleExternalCartSurface()) closeCartDrawer();
      }, 120);
    };
    settle();
    dedupeObserver = new MutationObserver(settle);
    dedupeObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'open'],
    });
    window.setTimeout(() => {
      dedupeObserver?.disconnect();
      dedupeObserver = null;
    }, 2500);
  };

  const dispatchCartChange = async () => {
    const cart = await fetchCart();

    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        detail: {cart},
        bubbles: true,
      })
    );

    document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
  };

  const setError = (root, message = '') => {
    const error = root.querySelector('[data-add-action-errors]');
    if (!error) return;
    error.textContent = message;
    error.classList.toggle('is-visible', Boolean(message));
  };

  const setupDraggableRow = (row) => {
    if (!row || row.dataset.reserveDragReady === 'true') return;
    row.dataset.reserveDragReady = 'true';

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragged = false;

    const isInteractiveTarget = (target) =>
      Boolean(target.closest('a, button, input, select, textarea, label, form, [role="button"]'));

    row.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0 || isInteractiveTarget(event.target)) return;
      if (row.scrollWidth <= row.clientWidth) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = row.scrollLeft;
      dragged = false;
      row.classList.add('is-dragging');
      row.setPointerCapture?.(pointerId);
    });

    row.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      if (Math.abs(deltaX) > 3) dragged = true;
      row.scrollLeft = startScrollLeft - deltaX;
      if (dragged) event.preventDefault();
    });

    const endDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      row.classList.remove('is-dragging');
      row.releasePointerCapture?.(pointerId);
      pointerId = null;
    };

    row.addEventListener('pointerup', endDrag);
    row.addEventListener('pointercancel', endDrag);
    row.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      },
      true
    );
  };

  const getSubscriptionPlanId = (root) => {
    const sellingPlanInput = root.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) return sellingPlanInput.value?.trim() || '';

    const purchaseInput = root.querySelector('[data-rr-purchase-option]:checked');
    const isSubscribing = Boolean(purchaseInput && purchaseInput.value === 'subscribe');
    return isSubscribing ? root.dataset.sellingPlanId || '' : '';
  };

  const getVariantDisplayPrice = (input, planId) => {
    if (planId) {
      try {
        const plans = JSON.parse(input.dataset.sellingPlanPrices || '{}');
        if (plans[planId]) return plans[planId];
      } catch (error) {
        console.warn('Invalid selling plan prices', error);
      }
    }
    return input.dataset.price || '';
  };

  const updatePrices = (root) => {
    const planId = getSubscriptionPlanId(root);

    root.querySelectorAll('[data-rr-variant-radio]').forEach((input) => {
      const price = getVariantDisplayPrice(input, planId);
      const sizePrice = input.closest('.rr-pdp__choice')?.querySelector('[data-rr-size-price]');
      if (sizePrice) sizePrice.textContent = price;
    });

    const selectedInput = root.querySelector('[data-rr-variant-radio]:checked');
    if (selectedInput) {
      const price = getVariantDisplayPrice(selectedInput, planId);
      root.querySelectorAll('[data-rr-selected-price]').forEach((el) => {
        el.textContent = price;
      });
    }
  };

  const syncVariant = (root, input) => {
    if (!input) return;

    root.dataset.selectedVariantId = input.value;

    updatePrices(root);

    const submit = root.querySelector('[data-rr-submit]');
    if (submit) submit.disabled = input.dataset.available === 'false';

    const mainImage = root.querySelector('[data-rr-main-image]');
    if (mainImage && input.dataset.image) mainImage.src = input.dataset.image;
  };

  const syncPurchaseOption = (root, input) => {
    if (!input) return;

    const sellingPlanInput = root.querySelector('[data-rr-selling-plan-input]');
    if (sellingPlanInput) {
      sellingPlanInput.value = input.value === 'subscribe' ? root.dataset.sellingPlanId || '' : '';
    }

    updatePrices(root);
  };

  roots.forEach((root) => {
    if (root.dataset.reserveReady === 'true') return;
    root.dataset.reserveReady = 'true';

    const form = root.querySelector('[data-rr-form]');
    const selectedVariant = root.querySelector('[data-rr-variant-radio]:checked');
    const selectedPurchase = root.querySelector('[data-rr-purchase-option]:checked');

    syncVariant(root, selectedVariant);
    syncPurchaseOption(root, selectedPurchase);

    root.querySelectorAll('[data-rr-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const image = thumb.querySelector('img');
        const mainImage = root.querySelector('[data-rr-main-image]');
        if (!image || !mainImage) return;

        mainImage.src = image.dataset.fullSrc || image.src;
        root.querySelectorAll('[data-rr-thumb]').forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    root.querySelectorAll('[data-rr-variant-radio]').forEach((input) => {
      input.addEventListener('change', () => syncVariant(root, input));
    });

    root.querySelectorAll('[data-rr-purchase-option]').forEach((input) => {
      input.addEventListener('change', () => syncPurchaseOption(root, input));
    });
    root.addEventListener('rosso:recharge-update', () => updatePrices(root));
    root.querySelectorAll('[data-rr-reward-tooltip-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        document.querySelectorAll('[data-rr-reward-tooltip-trigger][aria-expanded="true"]').forEach((other) => {
          if (other !== trigger) other.setAttribute('aria-expanded', 'false');
        });
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });

    root.querySelectorAll('.rr-pdp__review-row').forEach(setupDraggableRow);

    root.querySelectorAll('[data-rr-review-form]').forEach((reviewForm) => {
      reviewForm.addEventListener(
        'submit',
        async (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (reviewForm.dataset.reserveSubmitting === 'true') return;
          reviewForm.dataset.reserveSubmitting = 'true';

          const submit = reviewForm.querySelector('button[type="submit"]');
          const originalText = submit?.textContent || '';

          try {
            submit?.setAttribute('disabled', 'disabled');
            submit?.setAttribute('aria-busy', 'true');
            if (submit) submit.textContent = 'ADDING';

            const response = await fetch(`${cartBase()}/add.js`, {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
                Accept: 'application/json',
              },
              body: new FormData(reviewForm),
            });

            if (!response.ok) {
              const payload = await response.json().catch(() => ({}));
              throw new Error(payload.description || payload.message || 'Unable to add product');
            }

            await dispatchCartChange();
            dedupeCartSurfaces();

            if (submit) submit.textContent = 'ADDED';
          } catch (error) {
            console.warn(error);
            if (submit) submit.textContent = 'TRY AGAIN';
          } finally {
            window.setTimeout(() => {
              delete reviewForm.dataset.reserveSubmitting;
              submit?.removeAttribute('disabled');
              submit?.removeAttribute('aria-busy');
              if (submit) submit.textContent = originalText;
            }, 900);
          }
        },
        {capture: true}
      );
    });

    if (!form) return;

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (form.dataset.reserveSubmitting === 'true') return;
        form.dataset.reserveSubmitting = 'true';

        const submit = form.querySelector('[data-rr-submit]');
        const submitText = form.querySelector('[data-rr-submit-text]');
        const originalText = submitText?.textContent || '';

        try {
          setError(root);
          submit?.setAttribute('disabled', 'disabled');
          submit?.setAttribute('aria-busy', 'true');
          if (submitText) submitText.textContent = 'ADDING...';

          const response = await fetch(`${cartBase()}/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json',
            },
            body: new FormData(form),
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || payload.message || 'Unable to add bundle');
          }

          await dispatchCartChange();

          dedupeCartSurfaces();

          if (submitText) submitText.textContent = 'ADDED';
        } catch (error) {
          setError(root, error.message || 'Unable to add bundle');
          if (submitText) submitText.textContent = 'TRY AGAIN';
        } finally {
          window.setTimeout(() => {
            delete form.dataset.reserveSubmitting;
            submit?.removeAttribute('disabled');
            submit?.removeAttribute('aria-busy');
            if (submitText) submitText.textContent = originalText;
          }, 900);
        }
      },
      {capture: true}
    );
  });

  const closeRewardTooltips = () => {
    document.querySelectorAll('[data-rr-reward-tooltip-trigger][aria-expanded="true"]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-rr-reward-tooltip-trigger]')) return;
    closeRewardTooltips();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeRewardTooltips();
  });
})();
